/* Context-aware photos. No Instagram scraping, third-party tokens or dependencies. */
if (!customElements.get('bozhi-outfits-gallery')) {
  customElements.define('bozhi-outfits-gallery', class extends HTMLElement {
    connectedCallback() {
      if (this.events || this.mountFrame) return;
      this.mountFrame = requestAnimationFrame(() => { this.mountFrame = 0; this.mount(); });
    }
    mount() {
      if (!this.isConnected) return;
      this.events = new AbortController();
      const signal = this.events.signal;
      this.track = this.querySelector('.bozhi-outfits__track');
      this.content = this.querySelector('.bozhi-outfits__content');
      if (!this.track || !this.content) return;
      this.empty = this.querySelector('.bozhi-outfits__empty');
      this.retry = this.querySelector('[data-outfits-retry]');
      this.motionButton = this.querySelector('[data-outfits-motion]');
      this.labels = this.querySelector('[data-outfits-labels]')?.dataset || {};
      this.motion = matchMedia('(prefers-reduced-motion: reduce)');
      this.direction = 1;
      this.max = Math.min(50, Math.max(1, Number(this.dataset.limit) || 24));
      this.batch = Number(this.dataset.page) || 1;
      this.batches = Number(this.dataset.pages) || 1;
      this.userPaused = false;
      this.holdUntil = 0;
      this.track.addEventListener('scroll', () => this.updateArrows(), {passive:true,signal});
      this.track.addEventListener('wheel', () => this.interact(), {passive:true,signal});
      this.track.addEventListener('touchstart', () => this.interact(), {passive:true,signal});
      this.track.addEventListener('pointerdown', e => {
        this.interact();
        if (e.pointerType !== 'mouse' || e.button !== 0) return;
        this.drag = {id:e.pointerId, x:e.clientX, left:this.track.scrollLeft, moved:false};
      }, {signal});
      this.track.addEventListener('pointermove', e => {
        if (!this.drag || e.pointerId !== this.drag.id) return;
        const dx = e.clientX - this.drag.x;
        if (!this.drag.moved && Math.abs(dx) < 5) return;
        this.drag.moved = true;
        this.track.setPointerCapture(e.pointerId);
        this.track.classList.add('is-dragging');
        this.track.scrollLeft = this.drag.left - dx;
      }, {signal});
      const release = e => {
        if (!this.drag || e.pointerId !== this.drag.id) return;
        this.suppressClick = this.drag.moved;
        if (this.track.hasPointerCapture(e.pointerId)) this.track.releasePointerCapture(e.pointerId);
        this.track.classList.remove('is-dragging');
        this.drag = null;
        this.interact();
        clearTimeout(this.clickTimer);
        this.clickTimer = setTimeout(() => { this.suppressClick = false; }, 100);
      };
      this.track.addEventListener('pointerup', release, {signal});
      this.track.addEventListener('pointercancel', release, {signal});
      this.track.addEventListener('lostpointercapture', () => { this.drag = null; this.track.classList.remove('is-dragging'); }, {signal});
      this.track.addEventListener('dragstart', e => e.preventDefault(), {signal});
      this.track.addEventListener('click', e => { if (this.suppressClick) { e.preventDefault(); e.stopPropagation(); } }, {capture:true,signal});
      this.querySelectorAll('[data-outfits-direction]').forEach(button => button.addEventListener('click', () => {
        this.interact();
        this.go(this.track.scrollLeft + Number(button.dataset.outfitsDirection) * this.stepWidth());
      }, {signal}));
      this.track.addEventListener('keydown', e => {
        if (e.target !== this.track) return;
        const keys = {ArrowLeft:-1,ArrowRight:1};
        if (e.key in keys) { e.preventDefault(); this.interact(); this.go(this.track.scrollLeft + keys[e.key] * this.stepWidth()); }
        if (e.key === 'Home' || e.key === 'End') { e.preventDefault(); this.interact(); this.go(e.key === 'Home' ? 0 : this.end()); }
      }, {signal});
      this.addEventListener('pointerenter', () => { if (this.dataset.pauseHover === 'true') this.hovered = true; }, {signal});
      this.addEventListener('pointerleave', () => { this.hovered = false; this.startMotion(); }, {signal});
      this.addEventListener('focusin', () => { this.focused = true; }, {signal});
      this.addEventListener('focusout', () => queueMicrotask(() => { this.focused = this.contains(document.activeElement); this.startMotion(); }), {signal});
      this.motionButton?.addEventListener('click', () => {
        this.userPaused = !this.userPaused;
        this.motionButton.setAttribute('aria-pressed', String(this.userPaused));
        this.motionButton.textContent = this.userPaused ? this.labels.resume : this.labels.pause;
        this.startMotion();
      }, {signal});
      this.retry?.addEventListener('click', () => this.loadCollection(), {signal});
      this.motion.addEventListener('change', () => { this.refreshFooter(); this.startMotion(); }, {signal});
      document.addEventListener('visibilitychange', () => { if (!document.hidden) this.scheduleFooterRefresh(); this.startMotion(); }, {signal});
      window.addEventListener('resize', () => { this.updateArrows(); this.scheduleFooterRefresh(); }, {passive:true,signal});
      this.resize = new ResizeObserver(() => { this.updateArrows(); this.scheduleFooterRefresh(); });
      this.resize.observe(this.track);
      this.initFooterLifecycle(signal);

      // Observe the host, not the hidden empty track, so later collection batches still load.
      this.visibility = new IntersectionObserver(entries => {
        this.near = entries.some(e => e.isIntersecting);
        if (this.near) this.loadCollection();
        this.startMotion();
      }, {rootMargin:'800px 0px'});
      this.visibility.observe(this);
      this.visibleObserver = new IntersectionObserver(entries => {
        this.visible = entries.some(e => e.isIntersecting);
        this.startMotion();
      });
      this.visibleObserver.observe(this);
      this.resetNeeded = this.dataset.source === 'collection' && (this.batch !== 1 || Array.from(new URL(location.href).searchParams.keys()).some(key => key.startsWith('filter.') || key === 'sort_by'));
      if (this.resetNeeded) this.track.replaceChildren();
      this.refresh();
    }
    items() { return Array.from(this.track.querySelectorAll('.bozhi-outfits__item')); }
    end() { return Math.max(0, this.track.scrollWidth - this.track.clientWidth); }
    stepWidth() { return (this.items()[0]?.getBoundingClientRect().width || 180) + (parseFloat(getComputedStyle(this.track).gap) || 0); }
    interact() { this.holdUntil = performance.now() + 2500; this.lastStep = performance.now(); this.autoPosition = null; cancelAnimationFrame(this.scrollFrame); this.scrollFrame = 0; this.track.style.removeProperty('scroll-snap-type'); }
    updateArrows() {
      if (!this.track) return;
      const end = this.end();
      this.querySelectorAll('[data-outfits-direction]').forEach(button => {
        button.disabled = end <= 1 || (Number(button.dataset.outfitsDirection) < 0 ? this.track.scrollLeft <= 1 : this.track.scrollLeft >= end - 1);
      });
    }
    go(target) {
      cancelAnimationFrame(this.scrollFrame);
      const start = this.track.scrollLeft, end = Math.max(0, Math.min(this.end(), target));
      const duration = this.motion.matches ? 0 : Number(this.dataset.duration) || 450;
      if (!duration) { this.track.scrollLeft = end; return; }
      this.track.style.scrollSnapType = 'none';
      const began = performance.now();
      const step = now => {
        if (!this.isConnected) return;
        const t = Math.min(1,(now-began)/duration);
        this.track.scrollLeft = start + (end-start)*(1-Math.pow(1-t,3));
        if (t < 1) this.scrollFrame = requestAnimationFrame(step); else { this.scrollFrame = 0; this.track.style.removeProperty('scroll-snap-type'); }
      };
      this.scrollFrame = requestAnimationFrame(step);
    }
    startMotion() {
      cancelAnimationFrame(this.motionFrame);
      this.motionFrame = 0;
      this.lastTick = 0;
      if (!this.isConnected || document.hidden || !this.visible || this.motion?.matches || this.dataset.auto === 'off') return;
      const tick = now => {
        if (!this.isConnected || document.hidden || !this.visible || this.motion.matches) { this.motionFrame = 0; return; }
        const dt = this.lastTick ? Math.min(50,now-this.lastTick)/1000 : 0;
        this.lastTick = now;
        const end = this.end();
        const paused = this.userPaused || this.hovered || this.focused || this.drag || now < this.holdUntil || document.querySelector('dialog[open],cart-drawer.active');
        if (!paused && end > 1) {
          if (this.track.scrollLeft >= end-1) this.direction = -1;
          if (this.track.scrollLeft <= 1) this.direction = 1;
          if (this.dataset.auto === 'continuous') {
            this.autoPosition = Math.max(0, Math.min(end, (this.autoPosition ?? this.track.scrollLeft) + this.direction*(Number(this.dataset.speed)||24)*dt));
            this.track.scrollLeft = this.autoPosition;
          }
          else if (!this.scrollFrame && now-(this.lastStep||0) >= (Number(this.dataset.stepDelay)||4)*1000) {
            this.lastStep = now;
            this.go(this.track.scrollLeft + this.direction*this.stepWidth());
          }
        } else this.autoPosition = null;
        this.motionFrame = requestAnimationFrame(tick);
      };
      this.motionFrame = requestAnimationFrame(tick);
    }
    refresh() {
      const keys = new Set();
      this.items().forEach(item => {
        const key = item.dataset.outfitKey;
        if (keys.has(key) || keys.size >= this.max) item.remove(); else keys.add(key);
      });
      const count = this.items().length;
      this.content.hidden = count === 0;
      if (this.empty) this.empty.hidden = count > 0;
      this.dataset.count = String(count);
      this.updateArrows();
      this.refreshFooter();
      this.startMotion();
    }
    async loadCollection() {
      if (this.dataset.source !== 'collection' || this.loading || !this.isConnected) return;
      if (!this.resetNeeded && (this.items().length >= this.max || this.batch >= this.batches)) return;
      this.loading = true;
      this.retry.hidden = true;
      try {
        while (this.isConnected && this.items().length < this.max && (this.resetNeeded || this.batch < this.batches)) {
          const page = this.resetNeeded ? 1 : this.batch + 1;
          const url = new URL(location.href);
          url.hash = '';
          for (const key of Array.from(url.searchParams.keys())) {
            if (key.startsWith('filter.') || key === 'sort_by' || key === 'sections') url.searchParams.delete(key);
          }
          url.searchParams.set(this.dataset.pageParam || 'page', String(page));
          url.searchParams.set('section_id', this.dataset.sectionId);
          const response = await fetch(url.toString(), {credentials:'same-origin',signal:this.events.signal});
          if (!response.ok) throw new Error('Gallery page failed');
          const doc = new DOMParser().parseFromString(await response.text(),'text/html');
          const incoming = doc.querySelector('bozhi-outfits-gallery');
          if (!incoming || incoming.dataset.source !== 'collection' || Number(incoming.dataset.page) !== page) throw new Error('Invalid gallery page');
          if (this.resetNeeded) this.track.replaceChildren();
          this.track.append(...Array.from(incoming.querySelectorAll('.bozhi-outfits__item')));
          this.batch = page;
          this.batches = Number(incoming.dataset.pages)||page;
          this.resetNeeded = false;
          this.refresh();
        }
      } catch (error) {
        if (error.name !== 'AbortError' && this.isConnected) this.retry.hidden = false;
      } finally { this.loading = false; }
    }
    // Footer reveal v3: discover late/replaced sections without polling or page reloads.
    initFooterLifecycle(signal) {
      this.footerDesktop = matchMedia('(min-width: 750px)');
      this.footerDesktop.addEventListener('change', () => this.scheduleFooterRefresh(), {signal});
      ['load', 'pageshow'].forEach(name => window.addEventListener(name, () => this.scheduleFooterRefresh(), {signal}));
      document.addEventListener('DOMContentLoaded', () => this.scheduleFooterRefresh(), {once:true,signal});
      ['shopify:section:load', 'shopify:section:unload', 'shopify:section:reorder'].forEach(name => {
        document.addEventListener(name, () => this.scheduleFooterRefresh(), {signal});
      });
      document.fonts?.ready.then(() => { if (!signal.aborted) this.scheduleFooterRefresh(); });
      document.fonts?.addEventListener('loadingdone', () => this.scheduleFooterRefresh(), {signal});
      this.footerResize = new ResizeObserver(() => this.scheduleFooterRefresh());
      this.footerContentObserver = new MutationObserver(records => {
        // Only rebuild on structural changes. Our own per-scroll style writes are not observed.
        if (!this.footerPanel?.isConnected || records.some(record =>
          [...record.addedNodes, ...record.removedNodes].some(node => node.nodeType === 1 &&
            (node.matches('[data-ff20], .footer') || node.querySelector('[data-ff20], .footer')))
        )) this.scheduleFooterRefresh();
      });
      this.footerStructureObserver = new MutationObserver(() => this.scheduleFooterRefresh());
      const host = this.closest('.shopify-section');
      const main = document.getElementById('MainContent');
      // Shallow observation only: no document-wide subtree scan on carousel ticks.
      if (host?.parentElement) this.footerStructureObserver.observe(host.parentElement, {childList:true});
      if (main?.parentElement && main.parentElement !== host?.parentElement) {
        this.footerStructureObserver.observe(main.parentElement, {childList:true});
      }
      if (host) this.resize.observe(host);
    }
    scheduleFooterRefresh() {
      if (!this.isConnected || this.editorFrame) return;
      this.editorFrame = requestAnimationFrame(() => {
        this.editorFrame = 0;
        if (this.isConnected && this.events && !this.events.signal.aborted) this.refreshFooter();
      });
    }
    watchFooterCandidate(footer) {
      if (this.footerCandidate === footer) return;
      this.footerContentObserver?.disconnect();
      this.footerResize?.disconnect();
      this.footerCandidate = footer;
      if (footer) {
        this.footerContentObserver?.observe(footer, {childList:true,subtree:true});
        this.footerResize?.observe(footer);
      }
    }
    refreshFooter() {
      if (this.dataset.reveal !== 'true' || this.dataset.count === '0' || this.motion.matches || !this.footerDesktop?.matches) {
        this.releaseFooter(); this.watchFooterCandidate(null); return;
      }
      const host = this.closest('.shopify-section'), main = document.getElementById('MainContent');
      if (!host || !main) { this.releaseFooter(); this.watchFooterCandidate(null); return; }
      if (this.footerMain !== main) {
        if (this.footerMain) this.resize.unobserve(this.footerMain);
        this.footerMain = main;
        this.resize.observe(main);
      }
      let footer;
      if (host.parentElement === main) {
        const later = Array.from(main.children).slice(Array.from(main.children).indexOf(host) + 1).filter(el => el.getBoundingClientRect().height > 0);
        if (later.length) { this.releaseFooter(); this.watchFooterCandidate(null); return; }
        footer = main.nextElementSibling;
      } else footer = host.nextElementSibling;
      // Do not skip an unpainted footer: fonts/CSS may not have supplied its height yet.
      while (footer && !footer.matches('.shopify-section-group-footer-group') && footer.getBoundingClientRect().height === 0) footer = footer.nextElementSibling;
      if (!footer?.matches('.shopify-section-group-footer-group') || footer.querySelector('bozhi-outfits-gallery')) {
        this.releaseFooter(); this.watchFooterCandidate(null); return;
      }
      this.watchFooterCandidate(footer);
      const panel = footer.querySelector('[data-ff20], .footer');
      if (!panel) { this.releaseFooter(); return; }
      if (this.footer === footer && this.footerPanel === panel) { this.measureFooter(); return; }
      this.releaseFooter();
      const owner = window.__bozhiOutfitsFooterOwner;
      if (owner && owner !== this) {
        if (owner.isConnected) return;
        owner.releaseFooter?.();
      }
      this.footer = footer;
      this.footerPanel = panel;
      this.coverHost = host;
      const inheritedReveal = panel.classList.contains('bozhi-outfits-reveal-panel');
      this.oldRevealStyles = ['--outfits-reveal-shift', '--outfits-reveal-clip'].map(key =>
        [key, inheritedReveal ? '' : panel.style.getPropertyValue(key), panel.style.getPropertyPriority(key)]);
      window.__bozhiOutfitsFooterOwner = this;
      host.classList.add('bozhi-outfits-reveal-cover');
      footer.classList.add('bozhi-outfits-reveal-footer');
      panel.classList.add('bozhi-outfits-reveal-panel');
      this.dataset.revealVersion = '3';
      this.footerEvents = new AbortController();
      const signal = this.footerEvents.signal;
      // Native document scroll only. Horizontal gallery autoplay must not drive the footer.
      // Update in the scroll callback, not one additional animation frame later.
      window.addEventListener('scroll', () => this.measureFooter(), {passive:true,signal});
      footer.addEventListener('focusin', event => {
        // Only keyboard focus may uncover a hidden control; pointer focus must not jump the page.
        if (!event.target.matches?.(':focus-visible')) return;
        this.measureFooter();
        const bounds = event.target.getBoundingClientRect();
        const boundary = footer.getBoundingClientRect().top;
        if (bounds.top < Math.max(0, boundary) - 1 && boundary > 0) {
          const anchor = Math.max(0, this.footerViewport() - panel.getBoundingClientRect().height);
          window.scrollBy({top:Math.max(0, boundary - anchor), behavior:'instant'});
          this.measureFooter();
        }
      }, {signal});
      this.footerResize?.observe(panel);
      this.measureFooter();
    }
    footerViewport() {
      // The reveal belongs to the layout viewport, not the changing visual/pinch-zoom viewport.
      return document.documentElement.clientHeight || window.innerHeight;
    }
    scheduleFooter() {
      this.scheduleFooterRefresh();
    }
    measureFooter() {
      if (!this.footer || !this.footerPanel) return;
      if (!this.footerDesktop?.matches || this.motion.matches) { this.releaseFooter(); return; }
      if (!this.footer.isConnected || !this.footerPanel.isConnected) {
        this.releaseFooter(); this.scheduleFooterRefresh(); return;
      }
      const viewport = this.footerViewport();
      const height = this.footerPanel.getBoundingClientRect().height;
      const top = this.footer.getBoundingClientRect().top;
      if (!(viewport > 0 && height > 0) || !this.footer.getClientRects().length) {
        this.setFooterCovered(0); delete this.dataset.revealReady; return;
      }
      // Read the untransformed wrapper; never feed the moving panel's top back into its position.
      // Natural layout height stays intact. Tall footers scroll normally after the reveal.
      const anchor = Math.max(0, viewport - height);
      const covered = Math.round(Math.max(0, Math.min(height, viewport, top - anchor)) * 64) / 64;
      this.setFooterCovered(covered);
      if (this.dataset.revealReady !== 'true') this.dataset.revealReady = 'true';
    }
    setFooterCovered(covered) {
      if (!this.footerPanel || this.footerCovered === covered) return;
      this.footerCovered = covered;
      this.footerPanel.style.setProperty('--outfits-reveal-shift', `${-covered}px`);
      this.footerPanel.style.setProperty('--outfits-reveal-clip', `${covered}px`);
    }
    releaseFooter() {
      this.footerEvents?.abort();
      this.footerEvents = null;
      this.footerCovered = undefined;
      if (this.footerPanel) this.footerResize?.unobserve(this.footerPanel);
      this.coverHost?.classList.remove('bozhi-outfits-reveal-cover');
      this.footer?.classList.remove('bozhi-outfits-reveal-footer');
      this.footerPanel?.classList.remove('bozhi-outfits-reveal-panel');
      if (this.footerPanel) this.oldRevealStyles?.forEach(([key, value, priority]) => {
        if (value) this.footerPanel.style.setProperty(key, value, priority);
        else this.footerPanel.style.removeProperty(key);
      });
      if (window.__bozhiOutfitsFooterOwner === this) window.__bozhiOutfitsFooterOwner = null;
      delete this.dataset.revealReady;
      delete this.dataset.revealVersion;
      this.footer = null;
      this.footerPanel = null;
      this.coverHost = null;
      this.oldRevealStyles = null;
    }
    disconnectedCallback() {
      cancelAnimationFrame(this.mountFrame); this.mountFrame = 0;
      this.events?.abort(); this.events = null;
      cancelAnimationFrame(this.scrollFrame); this.scrollFrame = 0;
      cancelAnimationFrame(this.motionFrame); this.motionFrame = 0;
      clearTimeout(this.clickTimer);
      cancelAnimationFrame(this.editorFrame); this.editorFrame = 0;
      this.releaseFooter();
      this.footerStructureObserver?.disconnect();
      this.footerContentObserver?.disconnect();
      this.footerResize?.disconnect();
      this.footerCandidate = null;
      this.footerMain = null;
      this.resize?.disconnect();
      this.visibility?.disconnect();
      this.visibleObserver?.disconnect();
    }
  });
}
